import { Header } from '@/components/header/Header'
import { MatterOverviewChatInput } from '@/matter/components/matter-overview/MatterOverviewChatInput'
import { Edit, Settings, TrashDelete } from '@/assets/icons'
import { useMatterOverview } from '@/matter/components/matter-overview/useMatterOverview'
import { CollaborationHeaderActions } from '@/collaboration/components/CollaborationHeaderActions'
import { useCollaborationUpsell } from '@/collaboration/hooks/useCollaborationUpsell'
import { Card } from '@/components/card/Card'
import { DEVICE_TYPE, useHeaderDeviceType } from '@/components/header/hooks/useHeaderDeviceType'
import { useDynamicStickyHeaderTitle } from '@/matter/hooks/useDynamicStickyHeaderTitle'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import { canCreateChat, canDeleteMatter, canEditMatter, canEditMatterSettings, canViewChats } from '@/matter/utils/matter-permissions'
import { MatterOverviewBanner } from '@/matter/components/matter-overview/MatterOverviewBanner'
import { MatterHeader } from '@/matter/components/matter-overview/MatterHeader'
import { MatterContextRail } from '@/matter/components/matter-overview/MatterContextRail'
import { ActivityFeed } from '@/matter/activity-feed/components/ActivityFeed'

/** Px offset from the top of the scroll container to the title element — controls when the header title appears */
const TITLE_SCROLL_THRESHOLD_PX = 26

/**
 * Single-line "matter name • client" title for the page header below the two-column breakpoint.
 * Mirrors the Header's small string-title typography; the separator and client are muted.
 */
function MatterOverviewMobileHeaderTitle({ title, client }: { title: string; client: string | null }) {
  const text = client ? `${title} • ${client}` : title
  return (
    <span className="block truncate font-rethink-sans text-sm font-semibold not-italic leading-5 tracking-[-0.15px] text-brand-neutral-950" title={text}>
      {title}
      {client && <span className="text-brand-neutral-600"> • {client}</span>}
    </span>
  )
}

export default function MatterOverview() {
  const { matter, handleEditMatter, handleEditMatterJurisdiction, handleMatterSettings, handleDeleteMatter } = useMatterOverview()
  const { showUpsell, handleUpsellClick } = useCollaborationUpsell()
  const deviceType = useHeaderDeviceType()
  const isMobile = deviceType !== DEVICE_TYPE.DESKTOP

  const driveSyncEnabled = kGetFeatureFlagStatus(kFeatureFlags.DRIVE_SYNC)
  const title = matter?.name ?? 'Untitled' // All matters will have a name, this is just a defensive fallback

  // Use the display title from above so the matter overview correctly displays the title in the header when scrolled
  const { showTitle, stickyHeaderTitleRef } = useDynamicStickyHeaderTitle({
    title,
    scrollThreshold: TITLE_SCROLL_THRESHOLD_PX,
  })

  if (!matter) return null

  const userCanViewChats = canViewChats(matter)
  const chatDisabled = !canCreateChat(matter)
  const showChatInputBox = !chatDisabled

  return (
    <div className="flex min-h-0 flex-1 flex-col w-full min-w-0">
      {/* Header */}
      <Header
        titleSize="sm"
        title={
          // Matter details live in the page, so the header stays clear until the matter header
          // scrolls away.
          isMobile ? (
            // Below the two-column breakpoint the in-page matter header is hidden, so the page
            // header carries the matter and its client together on a single line instead.
            <MatterOverviewMobileHeaderTitle title={title} client={matter.client} />
          ) : showTitle ? (
            title
          ) : undefined
        }
        menuItems={[
          // Edit matter
          ...(canEditMatter(matter) ? [{ label: 'Edit', icon: Edit, onSelect: handleEditMatter }] : []),
          // Matter settings
          ...(driveSyncEnabled && canEditMatterSettings(matter) ? [{ label: 'Matter settings', icon: Settings, onSelect: handleMatterSettings }] : []),
          // Delete matter
          ...(canDeleteMatter(matter) ? [{ label: 'Delete', icon: TrashDelete, onSelect: handleDeleteMatter, destructive: true }] : []),
        ]}
        menuFooter={
          showUpsell
            ? {
                content: (
                  <Card
                    variant="upsell"
                    size="sm"
                    title="Add Team Members"
                    description="Create an organization to collaborate on this matter with colleagues."
                  />
                ),
                onSelect: handleUpsellClick,
              }
            : undefined
        }
        endContent={<CollaborationHeaderActions />}
        menuAriaLabel="Matter actions"
        borderBottom={showTitle}
      />

      <div className="flex flex-1 flex-col overflow-y-auto px-4 sm:px-12 lg:px-6 xl:px-12">
        <div className="mx-auto flex w-full min-w-0 max-w-[720px] flex-1 flex-col gap-6 pb-6 pt-3 lg:max-w-[1100px] lg:flex-row lg:items-start lg:pb-0 lg:pt-0 xl:gap-10">
          <div className="flex min-w-0 flex-1 flex-col lg:max-w-[720px]">
            {/* Not rendered below the two-column breakpoint, where the page header carries the
                matter instead. Hiding it with CSS would leave the sticky observer watching a
                display:none element, which never intersects and so pins the header permanently. */}
            {!isMobile && <MatterHeader matter={matter} containerRef={stickyHeaderTitleRef} />}
            {showChatInputBox && <MatterOverviewChatInput key={`chat-input-${matter.id}`} matterId={matter.id} />}
            <div className="mt-8 flex flex-col gap-4">
              {/* Below the two-column breakpoint the aside stacks under the feed, so the design
                  puts the banner here, as a row between the composer and the feed. */}
              {isMobile && <MatterOverviewBanner layout="row" />}
              {/* Remount per matter so the feed's reveal depth resets when switching matters. */}
              {userCanViewChats && <ActivityFeed key={matter.id} matter={matter} />}
            </div>
          </div>
          <aside className="flex w-full flex-col gap-4 lg:w-[278px] lg:shrink-0 xl:w-[340px]">
            <MatterContextRail matter={matter} onEditMatterDetails={() => handleEditMatter()} onEditJurisdiction={handleEditMatterJurisdiction} />
            {!isMobile && <MatterOverviewBanner layout="card" />}
          </aside>
        </div>
      </div>
    </div>
  )
}
